import React, { useState } from 'react';
import {
  Target, Globe, School, BookOpen, FileText, Calendar,
  Eye, Check, Users, Image, Info,
  AlertTriangle, Lock, Plus, X, GripVertical
} from 'lucide-react';
import './CreateCourseTab.css';
import WebBasedCourse from './Components/CreateCourseTab/WebBasedCourse';
import ILTCourse from './Components/CreateCourseTab/ILTCourse';
import BlendedCourse from './Components/CreateCourseTab/BlendedCourse';

interface Session {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  instructor: string;
  deliveryMethod: string;
  locationRoom: string;
  virtualMeetingLink: string;
}

interface Offering {
  id: number;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  timezone: string;
  totalSeats: number;
  enableWaitlist: boolean;
}

interface ContentLibraryItem {
  id: number;
  title: string;
  type: string;
  duration: string;
}

interface SelectedModule {
  id: number;
  title: string;
  type: string;
  duration: string;
}

const CreateCourseTab: React.FC = () => {
  const [courseType, setCourseType] = useState<'web' | 'ilt' | 'blended'>('ilt');
  const [courseTitle, setCourseTitle] = useState('');
  const [instructor, setInstructor] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [visibilityType, setVisibilityType] = useState<'public' | 'restricted'>('public');
  const [requiredModules, setRequiredModules] = useState(false);
  const [minimumScore, setMinimumScore] = useState('70');
  const [sequentialLearning, setSequentialLearning] = useState(false);
  const [managerApproval, setManagerApproval] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sendReminders, setSendReminders] = useState(false);
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [showOfferingForm, setShowOfferingForm] = useState(false);
  const [expandedOffering, setExpandedOffering] = useState<number | null>(null);

  // Course Modules state
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [selectedModules, setSelectedModules] = useState<SelectedModule[]>([]);
  const [tempSelectedModules, setTempSelectedModules] = useState<number[]>([]);

  // Offering form state
  const [offeringName, setOfferingName] = useState('');
  const [offeringLocation, setOfferingLocation] = useState('');
  const [offeringStartDate, setOfferingStartDate] = useState('');
  const [offeringEndDate, setOfferingEndDate] = useState('');
  const [offeringTimezone, setOfferingTimezone] = useState('IST');
  const [offeringSeats, setOfferingSeats] = useState('');
  const [enableWaitlist, setEnableWaitlist] = useState(false);

  // Sessions state (separate from offerings)
  const [sessions, setSessions] = useState<Session[]>([]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [sessionDate, setSessionDate] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState('');
  const [sessionEndTime, setSessionEndTime] = useState('');
  const [sessionInstructor, setSessionInstructor] = useState('');
  const [sessionDeliveryMethod, setSessionDeliveryMethod] = useState('Virtual');
  const [sessionLocationRoom, setSessionLocationRoom] = useState('');
  const [sessionVirtualLink, setSessionVirtualLink] = useState('');

  // Content Library Data
  const contentLibrary: ContentLibraryItem[] = [
    { id: 1, title: 'Introduction to Leadership Principles', type: 'Video', duration: '45 min' },
    { id: 2, title: 'Building High-Performance Teams', type: 'Interactive', duration: '60 min' },
    { id: 3, title: 'Strategic Decision Making', type: 'SCORM', duration: '90 min' },
    { id: 4, title: 'Data Analysis with Python', type: 'Video', duration: '120 min' },
    { id: 5, title: 'Effective Communication Skills', type: 'Interactive', duration: '50 min' },
    { id: 6, title: 'Project Management Fundamentals', type: 'SCORM', duration: '80 min' },
  ];

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleTargetAudienceChange = (audience: string) => {
    if (targetAudience.includes(audience)) {
      setTargetAudience(targetAudience.filter(a => a !== audience));
    } else {
      setTargetAudience([...targetAudience, audience]);
    }
  };

  const handleAddOffering = () => {
    if (!offeringName || !offeringLocation) {
      alert('Please fill in all required fields');
      return;
    }

    const newOffering: Offering = {
      id: offerings.length + 1,
      name: offeringName,
      location: offeringLocation,
      startDate: offeringStartDate,
      endDate: offeringEndDate,
      timezone: offeringTimezone,
      totalSeats: parseInt(offeringSeats) || 0,
      enableWaitlist,
    };

    setOfferings([...offerings, newOffering]);
    setShowOfferingForm(false);
    // Reset form
    setOfferingName('');
    setOfferingLocation('');
    setOfferingStartDate('');
    setOfferingEndDate('');
    setOfferingTimezone('IST');
    setOfferingSeats('');
    setEnableWaitlist(false);
  };

  const handleAddSession = () => {
    if (!sessionDate || !sessionStartTime || !sessionEndTime) {
      alert('Please fill in session date and times');
      return;
    }

    const newSession: Session = {
      id: sessions.length + 1,
      name: `Session ${sessions.length + 1}`,
      date: sessionDate,
      startTime: sessionStartTime,
      endTime: sessionEndTime,
      instructor: sessionInstructor,
      deliveryMethod: sessionDeliveryMethod,
      locationRoom: sessionLocationRoom,
      virtualMeetingLink: sessionVirtualLink,
    };

    setSessions([...sessions, newSession]);

    // Reset session form
    setShowSessionForm(false);
    setSessionDate('');
    setSessionStartTime('');
    setSessionEndTime('');
    setSessionInstructor('');
    setSessionDeliveryMethod('Virtual');
    setSessionLocationRoom('');
    setSessionVirtualLink('');
  };

  const handleDeleteSession = (sessionId: number) => {
    setSessions(sessions.filter(session => session.id !== sessionId));
  };

  const handleToggleModuleSelection = (moduleId: number) => {
    if (tempSelectedModules.includes(moduleId)) {
      setTempSelectedModules(tempSelectedModules.filter(id => id !== moduleId));
    } else {
      setTempSelectedModules([...tempSelectedModules, moduleId]);
    }
  };

  const handleOpenModulesModal = () => {
    setTempSelectedModules(selectedModules.map(m => m.id));
    setShowModulesModal(true);
  };

  const handleSaveModules = () => {
    const newSelectedModules = contentLibrary.filter(item => tempSelectedModules.includes(item.id));
    setSelectedModules(newSelectedModules);
    setShowModulesModal(false);
  };

  const handleRemoveModule = (moduleId: number) => {
    setSelectedModules(selectedModules.filter(m => m.id !== moduleId));
  };

  return (
    <div className="admin-coursemanagement-create-course-layout">
      {/* Main Content */}
      <div className="admin-coursemanagement-create-course-form">
        {/* Select Course Type */}
        <div className="admin-coursemanagement-form-section">
          <div className="admin-coursemanagement-section-header">
            <div className="admin-coursemanagement-section-icon"><Target size={24} color="white" /></div>
            <div>
              <h3>Select Course Type</h3>
            </div>
          </div>

          <div className="admin-coursemanagement-course-type-options">
            <button
              className={`admin-coursemanagement-course-type-card ${courseType === 'web' ? 'admin-coursemanagement-active' : ''}`}
              onClick={() => setCourseType('web')}
            >
              <div className="admin-coursemanagement-course-type-icon"><Globe size={32} /></div>
              <h4>Web-based Course</h4>
              <p>Self-paced online learning with modules and assessments</p>
            </button>

            <button
              className={`admin-coursemanagement-course-type-card ${courseType === 'ilt' ? 'admin-coursemanagement-active' : ''}`}
              onClick={() => setCourseType('ilt')}
            >
              <div className="admin-coursemanagement-course-type-icon"><School size={32} /></div>
              <h4>ILT Course</h4>
              <p>Instructor-led training with scheduled sessions and offerings</p>
            </button>

            <button
              className={`admin-coursemanagement-course-type-card ${courseType === 'blended' ? 'admin-coursemanagement-active' : ''}`}
              onClick={() => setCourseType('blended')}
            >
              <div className="admin-coursemanagement-course-type-icon"><BookOpen size={32} /></div>
              <h4>Blended Course</h4>
              <p>Combination of online modules and instructor-led sessions</p>
            </button>
          </div>
        </div>

        {/* Course Information */}
        <div className="admin-coursemanagement-form-section">
          <div className="admin-coursemanagement-section-header">
            <div className="admin-coursemanagement-section-icon admin-coursemanagement-blue"><FileText size={24} color="white" /></div>
            <div>
              <h3>Course Information</h3>
            </div>
          </div>

          <div className="admin-coursemanagement-form-group">
            <label>Course Title *</label>
            <input
              type="text"
              placeholder="e.g., Advanced Leadership Development"
              value={courseTitle}
              onChange={(e) => setCourseTitle(e.target.value)}
              className="admin-coursemanagement-form-input"
            />
          </div>

          {courseType === 'ilt' && (
            <div className="admin-coursemanagement-form-group">
              <label>Select Instructor</label>
              <input
                type="text"
                placeholder="Search & Select"
                value={instructor}
                onChange={(e) => setInstructor(e.target.value)}
                className="admin-coursemanagement-form-input"
              />
            </div>
          )}

          <div className="admin-coursemanagement-form-group">
            <label>Description</label>
            <textarea
              placeholder="Describe what learners will gain from this course..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="admin-coursemanagement-form-textarea"
              rows={4}
            />
          </div>

          <div className="admin-coursemanagement-form-row">
            <div className="admin-coursemanagement-form-group">
              <label>Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="admin-coursemanagement-form-select"
              >
                <option value="">Select</option>
                <option value="Leadership">Leadership</option>
                <option value="Technical">Technical</option>
                <option value="Management">Management</option>
              </select>
            </div>

            <div className="admin-coursemanagement-form-group">
              <label>Thumbnail</label>
              <button className="admin-coursemanagement-upload-btn">Upload Image</button>
            </div>
          </div>

          <div className="admin-coursemanagement-form-group">
            <label>Tags</label>
            <div className="admin-coursemanagement-tags-input-container">
              <div className="admin-coursemanagement-tags-list">
                {tags.map((tag, index) => (
                  <span key={index} className="admin-coursemanagement-tag">
                    {tag}
                    <button
                      onClick={() => setTags(tags.filter(t => t !== tag))}
                      className="admin-coursemanagement-tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="admin-coursemanagement-tag-input-wrapper">
                <input
                  type="text"
                  placeholder="Add a tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  className="admin-coursemanagement-tag-input"
                />
                <button onClick={handleAddTag} className="admin-coursemanagement-tag-add-btn">+</button>
              </div>
            </div>
          </div>
        </div>

        {/* Course Modules (Web-based Course) - Above Visibility & Access Control */}
        {courseType === 'web' && (
          <WebBasedCourse
            selectedModules={selectedModules}
            onOpenModulesModal={handleOpenModulesModal}
            onRemoveModule={handleRemoveModule}
          />
        )}

        {/* Offerings & Sessions (ILT Only) */}
        {courseType === 'ilt' && (
          <ILTCourse
            offerings={offerings}
            showOfferingForm={showOfferingForm}
            expandedOffering={expandedOffering}
            offeringName={offeringName}
            offeringLocation={offeringLocation}
            offeringStartDate={offeringStartDate}
            offeringEndDate={offeringEndDate}
            offeringTimezone={offeringTimezone}
            offeringSeats={offeringSeats}
            enableWaitlist={enableWaitlist}
            onShowOfferingFormToggle={() => setShowOfferingForm(!showOfferingForm)}
            onExpandedOfferingToggle={(id) => setExpandedOffering(id)}
            onOfferingNameChange={setOfferingName}
            onOfferingLocationChange={setOfferingLocation}
            onOfferingStartDateChange={setOfferingStartDate}
            onOfferingEndDateChange={setOfferingEndDate}
            onOfferingTimezoneChange={setOfferingTimezone}
            onOfferingSeatsChange={setOfferingSeats}
            onEnableWaitlistChange={setEnableWaitlist}
            onAddOffering={handleAddOffering}
            onDeleteOffering={(id) => setOfferings(offerings.filter(o => o.id !== id))}
            sessions={sessions}
            showSessionForm={showSessionForm}
            sessionDate={sessionDate}
            sessionStartTime={sessionStartTime}
            sessionEndTime={sessionEndTime}
            sessionInstructor={sessionInstructor}
            sessionDeliveryMethod={sessionDeliveryMethod}
            sessionLocationRoom={sessionLocationRoom}
            sessionVirtualLink={sessionVirtualLink}
            onShowSessionFormToggle={() => setShowSessionForm(!showSessionForm)}
            onSessionDateChange={setSessionDate}
            onSessionStartTimeChange={setSessionStartTime}
            onSessionEndTimeChange={setSessionEndTime}
            onSessionInstructorChange={setSessionInstructor}
            onSessionDeliveryMethodChange={setSessionDeliveryMethod}
            onSessionLocationRoomChange={setSessionLocationRoom}
            onSessionVirtualLinkChange={setSessionVirtualLink}
            onAddSession={handleAddSession}
            onDeleteSession={handleDeleteSession}
          />
        )}

        {/* Visibility & Access Control */}
        <div className="admin-coursemanagement-form-section">
          <div className="admin-coursemanagement-section-header">
            <div className="admin-coursemanagement-section-icon admin-coursemanagement-gray"><Eye size={24} color="white" /></div>
            <div>
              <h3>Visibility & Access Control</h3>
              <p className="admin-coursemanagement-section-subtitle">Control who can see and access this course</p>
            </div>
          </div>

          <div className="admin-coursemanagement-visibility-options">
            <button
              className={`admin-coursemanagement-visibility-option ${visibilityType === 'public' ? 'admin-coursemanagement-active' : ''}`}
              onClick={() => setVisibilityType('public')}
            >
              <div className="admin-coursemanagement-visibility-icon"><Globe size={24} /></div>
              <div className="admin-coursemanagement-visibility-content">
                <strong>Public / Organization-Wide</strong>
                <p>All users in your organization can see and access this course</p>
              </div>
            </button>

            <button
              className={`admin-coursemanagement-visibility-option ${visibilityType === 'restricted' ? 'admin-coursemanagement-active' : ''}`}
              onClick={() => setVisibilityType('restricted')}
            >
              <div className="admin-coursemanagement-visibility-icon"><Lock size={24} /></div>
              <div className="admin-coursemanagement-visibility-content">
                <strong>Restricted Access</strong>
                <p>Only specific groups, departments, or roles can access this course</p>
              </div>
            </button>
          </div>

          {visibilityType === 'public' && (
            <div className="admin-coursemanagement-info-box admin-coursemanagement-green">
              <span className="admin-coursemanagement-info-icon"><Globe size={16} /></span>
              <div>
                <p>This course will be visible and accessible to all users in your organization.</p>
              </div>
            </div>
          )}

          {visibilityType === 'restricted' && (
            <>
              <div className="admin-coursemanagement-info-box admin-coursemanagement-orange-box">
                <span className="admin-coursemanagement-info-icon"><AlertTriangle size={16} /></span>
                <div>
                  <strong>Configure Access Restrictions</strong>
                  <p>Select specific groups, departments, or user roles that should have access.</p>
                </div>
              </div>

              <div className="admin-coursemanagement-form-group">
                <label>Groups</label>
                <select className="admin-coursemanagement-form-select">
                  <option>Select groups...</option>
                </select>
              </div>

              <div className="admin-coursemanagement-form-group">
                <label>Departments</label>
                <select className="admin-coursemanagement-form-select">
                  <option>Select departments...</option>
                </select>
              </div>

              <div className="admin-coursemanagement-form-group">
                <label>User Roles</label>
                <select className="admin-coursemanagement-form-select">
                  <option>Select roles...</option>
                </select>
              </div>

              <div className="admin-coursemanagement-info-box admin-coursemanagement-yellow-box">
                <span className="admin-coursemanagement-info-icon"><Info size={16} /></span>
                <div>
                  <strong>No access restrictions configured</strong>
                  <p>Please select at least one group, department, or role.</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Course Modules (Blended Course) - After Visibility & Access Control */}
        {courseType === 'blended' && (
          <BlendedCourse
            selectedModules={selectedModules}
            onOpenModulesModal={handleOpenModulesModal}
            onRemoveModule={handleRemoveModule}
          />
        )}

        {/* Completion Rules */}
        <div className="admin-coursemanagement-form-section">
          <div className="admin-coursemanagement-section-header">
            <div className="admin-coursemanagement-section-icon admin-coursemanagement-green"><Check size={24} color="white" /></div>
            <div>
              <h3>Completion Rules</h3>
            </div>
          </div>

          <div className="admin-coursemanagement-completion-toggles">
            <div className="admin-coursemanagement-toggle-row">
              <div>
                <strong>Required Modules</strong>
                <p>All modules must be completed</p>
              </div>
              <label className="admin-coursemanagement-toggle-switch">
                <input
                  type="checkbox"
                  checked={requiredModules}
                  onChange={(e) => setRequiredModules(e.target.checked)}
                />
                <span className="admin-coursemanagement-toggle-slider"></span>
              </label>
            </div>

            <div className="admin-coursemanagement-form-group">
              <label>Minimum Score (%)</label>
              <input
                type="number"
                value={minimumScore}
                onChange={(e) => setMinimumScore(e.target.value)}
                className="admin-coursemanagement-form-input"
              />
            </div>

            <div className="admin-coursemanagement-toggle-row">
              <div>
                <strong>Sequential Learning</strong>
                <p>Modules must be completed in order</p>
              </div>
              <label className="admin-coursemanagement-toggle-switch">
                <input
                  type="checkbox"
                  checked={sequentialLearning}
                  onChange={(e) => setSequentialLearning(e.target.checked)}
                />
                <span className="admin-coursemanagement-toggle-slider"></span>
              </label>
            </div>

            <div className="admin-coursemanagement-toggle-row">
              <div>
                <strong>Manager Approval</strong>
                <p>Requires permission from manager</p>
              </div>
              <label className="admin-coursemanagement-toggle-switch">
                <input
                  type="checkbox"
                  checked={managerApproval}
                  onChange={(e) => setManagerApproval(e.target.checked)}
                />
                <span className="admin-coursemanagement-toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Enrollment Settings */}
        <div className="admin-coursemanagement-form-section">
          <div className="admin-coursemanagement-section-header">
            <div className="admin-coursemanagement-section-icon admin-coursemanagement-blue"><Users size={24} color="white" /></div>
            <div>
              <h3>Enrollment Settings</h3>
            </div>
          </div>

          <div className="admin-coursemanagement-form-group">
            <label>Enrollment Type</label>
            <select
              value={enrollmentType}
              onChange={(e) => setEnrollmentType(e.target.value)}
              className="admin-coursemanagement-form-select"
            >
              <option value="">Select</option>
              <option value="open">Open Enrollment</option>
              <option value="restricted">Restricted</option>
              <option value="auto">Auto-Enrollment</option>
            </select>
          </div>

          <div className="admin-coursemanagement-form-row">
            <div className="admin-coursemanagement-form-group">
              <label>Due Date (Optional)</label>
              <input
                type="text"
                placeholder="dd-mm-yyyy"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="admin-coursemanagement-form-input"
              />
            </div>
            <div className="admin-coursemanagement-form-group" style={{ display: 'flex', flexDirection: 'column' }}>
              <label>Email Reminders</label>
              <label className="admin-coursemanagement-toggle-switch">
                <input
                  type="checkbox"
                  checked={sendReminders}
                  onChange={(e) => setSendReminders(e.target.checked)}
                />
                <span className="admin-coursemanagement-toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="admin-coursemanagement-form-group">
            <label>Survey Type</label>
            <select className="admin-coursemanagement-form-select">
              <option>Search & Select survey type after completing this course</option>
            </select>
          </div>

          <div className="admin-coursemanagement-form-actions" style={{ marginTop: '24px' }}>
            <button className="admin-coursemanagement-btn-primary" style={{ width: '100%' }}>
              Create Course
            </button>
          </div>
        </div>
      </div>

      {/* Preview Sidebar */}
      <div className="admin-coursemanagement-course-preview-sidebar">
        <h3>Course Preview</h3>

        <div className="admin-coursemanagement-preview-card">
          <div className="admin-coursemanagement-preview-upload">
            <div className="admin-coursemanagement-preview-upload-icon"><Image size={32} /></div>
          </div>

          <h4>{courseTitle || 'Course Title'}</h4>
          <p className="admin-coursemanagement-preview-description">
            {description || 'Course description will appear here...'}
          </p>

          <div className="admin-coursemanagement-preview-details">
            <div className="admin-coursemanagement-preview-detail">
              <span className="admin-coursemanagement-preview-label">Type:</span>
              <span className="admin-coursemanagement-preview-badge">
                {courseType === 'web' ? 'Web-based' : courseType === 'ilt' ? 'ILT' : 'Blended'}
              </span>
            </div>
            <div className="admin-coursemanagement-preview-detail">
              <span className="admin-coursemanagement-preview-label">Category:</span>
              <span className="admin-coursemanagement-preview-value">{category || 'Not set'}</span>
            </div>
            {courseType === 'ilt' && (
              <div className="admin-coursemanagement-preview-detail">
                <span className="admin-coursemanagement-preview-label">Offerings:</span>
                <span className="admin-coursemanagement-preview-value">{offerings.length}</span>
              </div>
            )}
          </div>

          <button className="admin-coursemanagement-preview-create-btn">Create Course</button>
          <button className="admin-coursemanagement-preview-cancel-btn">Cancel</button>
        </div>
      </div>

      {/* Add Modules Modal */}
      {showModulesModal && (
        <div className="admin-coursemanagement-modal-overlay" onClick={() => setShowModulesModal(false)}>
          <div className="admin-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-coursemanagement-modal-header">
              <h2>Add Modules</h2>
              <button
                className="admin-coursemanagement-icon-btn"
                onClick={() => setShowModulesModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="admin-coursemanagement-modal-body">
              {/* Select from Content Library */}
              <div className="admin-coursemanagement-modal-section">
                <h3>Select from Content Library</h3>
                <div className="admin-coursemanagement-content-library-list">
                  {contentLibrary.map((item) => (
                    <div
                      key={item.id}
                      className={`admin-coursemanagement-content-library-item ${
                        tempSelectedModules.includes(item.id) ? 'admin-coursemanagement-selected' : ''
                      }`}
                      onClick={() => handleToggleModuleSelection(item.id)}
                    >
                      <div className="admin-coursemanagement-content-library-icon">
                        <FileText size={24} color="#FF8C00" />
                      </div>
                      <div className="admin-coursemanagement-content-library-info">
                        <p className="admin-coursemanagement-content-library-title">{item.title}</p>
                        <p className="admin-coursemanagement-content-library-meta">
                          {item.type} • {item.duration}
                        </p>
                      </div>
                      {tempSelectedModules.includes(item.id) && (
                        <div className="admin-coursemanagement-content-library-check">
                          <Check size={20} color="#FF8C00" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Modules Preview */}
              {tempSelectedModules.length > 0 && (
                <div className="admin-coursemanagement-modal-section">
                  <h3>Selected Modules ({tempSelectedModules.length})</h3>
                  <div className="admin-coursemanagement-selected-modules-preview">
                    {contentLibrary
                      .filter((item) => tempSelectedModules.includes(item.id))
                      .map((item, index) => (
                        <div key={item.id} className="admin-coursemanagement-selected-module-preview-item">
                          <div className="admin-coursemanagement-module-drag">
                            <GripVertical size={16} />
                          </div>
                          <span className="admin-coursemanagement-module-number">{index + 1}.</span>
                          <div className="admin-coursemanagement-module-icon-small">
                            <FileText size={20} color="#FF8C00" />
                          </div>
                          <div className="admin-coursemanagement-module-info-small">
                            <p>{item.title}</p>
                            <p>{item.type} • {item.duration}</p>
                          </div>
                          <button
                            className="admin-coursemanagement-icon-btn"
                            onClick={() => handleToggleModuleSelection(item.id)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="admin-coursemanagement-modal-footer">
              <button
                className="admin-coursemanagement-btn-secondary"
                onClick={() => setShowModulesModal(false)}
              >
                Cancel
              </button>
              <button
                className="admin-coursemanagement-btn-primary"
                onClick={handleSaveModules}
              >
                Add Selected Modules
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCourseTab;